import React, { useEffect, useState } from 'react'
import Select, { SingleValue } from 'react-select'

import { convertTo12HourFormat, convertTo24HourFormat } from '../../../../../../helpers/time'
import { useOutsideClick } from '../../../../../../hooks/useOutsideClick'

import { rideTimeSelectStyles } from '../../../../../../customStyles/ReactSelectCustomStyle'
import {
  HOURS_12_OPTIONS,
  MINUTES_OPTIONS,
  PERIODS_OPTIONS,
  SECONDS_OPTIONS,
} from '../../../../../../fakeData/TimeList'
import { cn } from '../../../../../../lib/utils'

interface RideTimeSelectorProps {
  initialTime?: string
  saveHandler: (time: string) => void
  placeholder?: string
  hasClash?: boolean
}

const RideTimeSelector: React.FC<RideTimeSelectorProps> = ({
  initialTime,
  saveHandler,
  placeholder = 'Set Time',
  hasClash = false,
}) => {
  const [editMode, setEditMode] = useState(false)
  const [isEditingPending, setIsEditingPending] = useState(false)
  const ref = useOutsideClick(() => {
    setEditMode(false)
  })

  const [selectedTime, setSelectedTime] = useState<{
    hour: string
    minute: string
    second: string
    period: string
  }>(() =>
    initialTime
      ? convertTo12HourFormat(initialTime)
      : { hour: '00', minute: '00', second: '00', period: 'AM' }
  )

  const handleOpenEditMode = () => {
    if (!initialTime) {
      setSelectedTime({ hour: '08', minute: '00', second: '00', period: 'AM' })
      setIsEditingPending(true)
    } else {
      setSelectedTime(convertTo12HourFormat(initialTime))
      setEditMode(true)
    }
  }

  useEffect(() => {
    if (isEditingPending) {
      setEditMode(true)
      setIsEditingPending(false)
    }
  }, [selectedTime, isEditingPending])

  const handleTimeChange = (
    type: 'hour' | 'minute' | 'second' | 'period',
    option: SingleValue<{ value: string; label: string }>
  ) => {
    if (option) {
      setSelectedTime((prev) => ({ ...prev, [type]: option.value }))
    }
  }

  const handleSave = () => {
    const formattedTime = convertTo24HourFormat(
      selectedTime.hour,
      selectedTime.minute,
      selectedTime.second,
      selectedTime.period
    )
    saveHandler(formattedTime)
    setEditMode(false)
  }

  if (!editMode) {
    const timeDisplay = initialTime
      ? `${selectedTime.hour}:${selectedTime.minute}:${selectedTime.second} ${selectedTime.period}`
      : placeholder

    return (
      <div
        className={cn(
          'cursor-pointer text-center min-w-[100px] underline hover:no-underline',
          hasClash && 'decoration-red-600'
        )}
        onClick={(e) => {
          e.stopPropagation()
          handleOpenEditMode()
        }}
      >
        <span className={hasClash ? 'text-red-600' : 'text-gray-700'}>{timeDisplay}</span>
      </div>
    )
  }

  return (
    <div
      ref={ref}
      className={cn(
        'cursor-pointer flex items-center text-center underline hover:no-underline',
        editMode && 'no-underline',
        hasClash && 'decoration-red-600'
      )}
    >
      <div onClick={(e) => e.stopPropagation()} className="flex w-full">
        <Select
          classNamePrefix="select"
          options={HOURS_12_OPTIONS}
          value={HOURS_12_OPTIONS.find((h) => h.value === selectedTime.hour)}
          onChange={(opt) =>
            handleTimeChange('hour', opt as SingleValue<{ value: string; label: string }>)
          }
          styles={rideTimeSelectStyles as any}
          className="flex-1 px-[0!important] h-[40px] bg-[#f0f0f0] flex items-center outline-0 text-gray-500 w-full focus:ring-0 focus:outline-0 no-underline rounded-l-[4px]"
          isClearable={false}
          components={{ DropdownIndicator: null }}
          isSearchable={true}
        />
      </div>

      <div onClick={(e) => e.stopPropagation()} className="flex w-full">
        <Select
          classNamePrefix="select"
          options={MINUTES_OPTIONS}
          value={MINUTES_OPTIONS.find((m) => m.value === selectedTime.minute)}
          onChange={(opt) =>
            handleTimeChange('minute', opt as SingleValue<{ value: string; label: string }>)
          }
          styles={rideTimeSelectStyles as any}
          className="flex-1 px-[0!important] h-[40px] border-none flex items-center outline-0 text-gray-500 w-full focus:ring-0 focus:border-0 focus:outline-0 bg-[#f0f0f0] !rounded-none"
          isClearable={false}
          components={{ DropdownIndicator: null }}
          isSearchable={true}
        />
      </div>

      <div onClick={(e) => e.stopPropagation()} className="flex w-full">
        <Select
          classNamePrefix="select"
          options={SECONDS_OPTIONS}
          value={SECONDS_OPTIONS.find((s) => s.value === selectedTime.second)}
          onChange={(opt) => handleTimeChange('second', opt)}
          styles={rideTimeSelectStyles as any}
          className="flex-1 h-[40px] border-none flex items-center outline-0 text-gray-500 w-full focus:ring-0 focus:border-0 focus:outline-0 bg-[#f0f0f0] !rounded-none"
          isClearable={false}
          isSearchable
        />
      </div>

      <div onClick={(e) => e.stopPropagation()} className="flex w-full">
        <Select
          classNamePrefix="select"
          options={PERIODS_OPTIONS}
          value={PERIODS_OPTIONS.find((p) => p.value === selectedTime.period)}
          onChange={(opt) =>
            handleTimeChange('period', opt as SingleValue<{ value: string; label: string }>)
          }
          styles={rideTimeSelectStyles as any}
          className="flex-1 px-[0!important] h-[40px] outline-0 flex items-center text-gray-500 w-full focus:ring-0 focus:border-0 focus:outline-0 bg-[#f0f0f0] rounded-r-[4px]"
          isClearable={false}
          components={{ DropdownIndicator: null }}
          isSearchable={true}
        />
      </div>

      <button
        className="ms-3 text-[12px] text-gray-600 hover:opacity-50 transition-all"
        onClick={() => setEditMode(false)}
      >
        Cancel
      </button>
      <button
        className="ms-3 me-5 text-SeabiscuitMainThemeColor text-[12px] font-medium hover:opacity-50 transition-all"
        onClick={handleSave}
      >
        Save
      </button>
    </div>
  )
}

export default RideTimeSelector
